// Phase SIKA-4a — Goal Portfolio (multi-goal dashboard).
//
// Source: SIKA_Independence_Architecture_v1.0 §07 (Multi-Goal Portfolio +
// Table 20) + §11 N05 (same-candidate banner) + §11 N10 (completed
// archive permanence) + §11 N12 (combined bankroll advisory, real
// aggregation across all active goals).
//
// Doctrine:
//   N05 — Same-candidate overlap renders a banner. Promotion-not-restriction
//         copy: "Both your X and Y plans have selected Z … consider whether
//         you want double exposure." Never blocks.
//   N10 — Completed goals never disappear. Collapsible "Completed Goals"
//         section shows them as read-only summary cards. No delete UI.
//   N12 — Combined-stake advisory aggregates ALL active goals' current
//         cycle stakes against the user's declared bankroll.
//   No primitive edits. Compositions only.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import { useComposedQuery, useTableQuery } from "../../data/useTableQuery.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import {
  computeWeeklyExposure,
  formatExposureAdvisory,
  type SikaActiveGoalForExposure,
} from "../../sika/checkBankrollExposure.ts";
import {
  findSameCandidateOverlap,
  formatSameCandidateBanner,
  type SameCandidateLeg,
} from "../../sika/detectSameCandidate.ts";
import { Badge, Eyebrow, HairlineRow, Pill } from "../../components/super-acca";
import { VerifiedReturnCertificate } from "../../components/sika-extras/VerifiedReturnCertificate.tsx";
import { CertificateShareButton } from "../../components/sika-extras/CertificateShareButton.tsx";

const RECOMMENDED_MAX_ACTIVE_GOALS = 3;

interface PortfolioCycleRow {
  cycle_id: string;
  goal_id: string;
  status: string;
  outcome: string | null;
  stake_amount: number | null;
  cycle_number: number;
}

interface PortfolioLegRow {
  leg_id: string;
  cycle_id: string;
  goal_id: string;
  fixture_id: string | null;
  market: string | null;
  pick: string | null;
}

export function SikaPortfolioPage(): JSX.Element {
  const { user } = useAuthSession();
  const { goals, loading } = useSikaGoals();
  const [showCompleted, setShowCompleted] = useState(false);
  const [busyGoalId, setBusyGoalId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const active = useMemo(
    () => goals.filter((g) => g.status === "active" || g.status === "in_progress"),
    [goals],
  );
  const paused = useMemo(() => goals.filter((g) => g.status === "paused"), [goals]);
  const completed = useMemo(
    () => goals.filter((g) => g.status === "completed" || g.status === "achieved"),
    [goals],
  );

  const userId = user?.id ?? null;
  const goalIds = useMemo(() => active.map((g) => g.id), [active]);

  // Load cycles + legs for active goals (for exposure + N05 detection).
  const cyclesLegsQuery = useComposedQuery<{ cycles: PortfolioCycleRow[]; legs: PortfolioLegRow[] }>(
    ["sika-portfolio-cycles-legs", userId, goalIds],
    async (client) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supabase = client as any;
      const { data: cyc } = await supabase
        .from("sika_weekly_cycles")
        .select("cycle_id,goal_id,status,outcome,stake_amount,cycle_number")
        .in("goal_id", goalIds)
        .in("status", ["published", "locked"]);
      const cycleRows = (cyc ?? []) as PortfolioCycleRow[];
      if (cycleRows.length === 0) {
        return { cycles: cycleRows, legs: [] };
      }
      const cycleIds = cycleRows.map((c) => c.cycle_id);
      const { data: lg } = await supabase
        .from("sika_cycle_legs")
        .select("leg_id,cycle_id,fixture_id,market,pick")
        .in("cycle_id", cycleIds);
      const cycleToGoal = new Map(cycleRows.map((c) => [c.cycle_id, c.goal_id]));
      const legRows: PortfolioLegRow[] = ((lg ?? []) as Array<{
        leg_id: string;
        cycle_id: string;
        fixture_id: string | null;
        market: string | null;
        pick: string | null;
      }>).map((r) => ({
        leg_id: r.leg_id,
        cycle_id: r.cycle_id,
        goal_id: cycleToGoal.get(r.cycle_id) ?? "",
        fixture_id: r.fixture_id,
        market: r.market,
        pick: r.pick,
      }));
      return { cycles: cycleRows, legs: legRows };
    },
    { enabled: Boolean(userId) && goalIds.length > 0 },
  );
  const cycles = cyclesLegsQuery.data?.cycles ?? [];
  const legs = cyclesLegsQuery.data?.legs ?? [];

  // Load bankroll (N12).
  const bankrollQuery = useTableQuery<{ total_bankroll: number | null } | null>(
    ["sika-portfolio-bankroll", userId],
    (client) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (client as any)
        .from("user_bankrolls")
        .select("total_bankroll")
        .eq("user_id", userId)
        .maybeSingle(),
    { enabled: Boolean(userId) },
  );
  const bankroll = typeof bankrollQuery.data?.total_bankroll === "number"
    ? bankrollQuery.data.total_bankroll
    : null;

  // N12 — combined exposure across ALL active goals.
  const exposureGoals: SikaActiveGoalForExposure[] = useMemo(() => {
    const stakeByGoal = new Map<string, number>();
    for (const c of cycles) {
      const s = c.stake_amount;
      if (typeof s === "number" && s > 0) {
        const prev = stakeByGoal.get(c.goal_id) ?? 0;
        if (s > prev) stakeByGoal.set(c.goal_id, s);
      }
    }
    return active.map((g) => ({ id: g.id, weekly_stake: stakeByGoal.get(g.id) ?? null }));
  }, [active, cycles]);
  const exposure = computeWeeklyExposure(exposureGoals, bankroll);

  // N05 — same-candidate overlap detection across goals.
  const goalNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);
  const overlapLegs: SameCandidateLeg[] = useMemo(
    () => legs.map((l) => ({
      leg_id: l.leg_id,
      goal_id: l.goal_id,
      goal_name: goalNameById.get(l.goal_id) ?? "Unnamed goal",
      fixture_id: l.fixture_id,
      market: l.market,
      pick: l.pick,
    })),
    [legs, goalNameById],
  );
  const overlaps = useMemo(() => findSameCandidateOverlap(overlapLegs), [overlapLegs]);

  async function handlePause(goalId: string): Promise<void> {
    setBusyGoalId(goalId);
    setActionError(null);
    const supabase = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any).rpc("sika_pause_goal", { p_goal_id: goalId });
    setBusyGoalId(null);
    if (error) {
      setActionError(error.message);
      return;
    }
    void cyclesLegsQuery.refetch();
  }
  async function handleResume(goalId: string): Promise<void> {
    setBusyGoalId(goalId);
    setActionError(null);
    const supabase = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any).rpc("sika_resume_goal", { p_goal_id: goalId });
    setBusyGoalId(null);
    if (error) {
      setActionError(error.message);
      return;
    }
    void cyclesLegsQuery.refetch();
  }

  const overActive = active.length > RECOMMENDED_MAX_ACTIVE_GOALS;

  return (
    <div
      data-testid="sika-portfolio"
      className="sa-root"
      style={{
        padding: 24,
        maxWidth: 1100,
        margin: "0 auto",
        color: "var(--sa-fg-primary)",
        background: "var(--sa-bg-base)",
        minHeight: "100%",
        fontFamily: "var(--sa-font-body)",
      }}
    >
      <header style={{ marginBottom: 20 }}>
        <Eyebrow>SIKA · All Goals · {String(active.length)} active</Eyebrow>
        <h1 style={{ margin: "8px 0 4px", fontFamily: "var(--sa-font-display)", fontSize: 28 }}>
          Goal Portfolio
        </h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Pill>{String(goals.length)} total</Pill>
          <Pill>{String(active.length)} active</Pill>
          {paused.length > 0 ? <Pill>{String(paused.length)} paused</Pill> : null}
          {completed.length > 0 ? <Pill>{String(completed.length)} completed</Pill> : null}
        </div>
      </header>

      {overActive ? (
        <div
          data-testid="portfolio-over-max-banner"
          style={{
            padding: 12,
            marginBottom: 12,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            background: "var(--sa-bg-elev)",
          }}
        >
          <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
            Advisory
          </Pill>
          <p style={{ margin: "8px 0 0", fontSize: 13 }}>
            Recommended max {String(RECOMMENDED_MAX_ACTIVE_GOALS)} active goals — attention and
            bankroll may be stretched across {String(active.length)} goals. SIKA flags this; the
            decision is yours.
          </p>
        </div>
      ) : null}

      {exposure.breach || exposure.reason === "no_bankroll_declared" ? (
        <div
          data-testid="portfolio-bankroll-advisory"
          style={{
            padding: 12,
            marginBottom: 12,
            border: `1px solid ${exposure.breach ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
            borderRadius: 4,
            background: "var(--sa-bg-elev)",
          }}
        >
          <Pill>Combined Bankroll · N12</Pill>
          <p style={{ margin: "8px 0 0", fontSize: 13 }}>{formatExposureAdvisory(exposure)}</p>
        </div>
      ) : null}

      {overlaps.length > 0 ? (
        <div
          data-testid="portfolio-same-candidate-banner"
          style={{
            padding: 12,
            marginBottom: 12,
            border: "1px solid var(--sa-accent-saffron)",
            borderRadius: 4,
            background: "var(--sa-bg-elev)",
          }}
        >
          <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
            Same-candidate · N05
          </Pill>
          {overlaps.map((f) => (
            <p
              key={`${f.fixture_id}-${f.market}-${f.pick}`}
              style={{ margin: "8px 0 0", fontSize: 13 }}
            >
              {formatSameCandidateBanner(f)}
            </p>
          ))}
        </div>
      ) : null}

      {actionError ? (
        <div
          role="alert"
          style={{ padding: 8, marginBottom: 8, fontSize: 12, color: "var(--sa-accent-saffron)" }}
        >
          {actionError}
        </div>
      ) : null}

      {loading ? (
        <p>Loading goals…</p>
      ) : goals.length === 0 ? (
        <p>No goals yet. Start a conversation with SIKA to create your first goal.</p>
      ) : (
        <>
          <Section title="Active">
            {active.length === 0 ? (
              <p style={{ fontSize: 13, color: "var(--sa-fg-secondary)" }}>No active goals.</p>
            ) : (
              <Grid>
                {active.map((g) => (
                  <PortfolioCard
                    key={g.id}
                    goal={g}
                    onPause={() => void handlePause(g.id)}
                    busy={busyGoalId === g.id}
                  />
                ))}
              </Grid>
            )}
          </Section>

          {paused.length > 0 ? (
            <Section title="Paused">
              <Grid>
                {paused.map((g) => (
                  <PortfolioCard
                    key={g.id}
                    goal={g}
                    onResume={() => void handleResume(g.id)}
                    busy={busyGoalId === g.id}
                  />
                ))}
              </Grid>
            </Section>
          ) : null}

          {completed.length > 0 ? (
            <Section
              title={`Completed Goals · ${String(completed.length)}`}
              collapsible
              expanded={showCompleted}
              onToggle={() => setShowCompleted((v) => !v)}
            >
              {showCompleted ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                  data-testid="portfolio-completed-list"
                >
                  {completed.map((g) => (
                    <div
                      key={g.id}
                      style={{ display: "flex", flexDirection: "column", gap: 10 }}
                      data-testid={`portfolio-completed-${g.id}`}
                    >
                      <PortfolioCard goal={g} readOnly />
                      <VerifiedReturnCertificate goalId={g.id} isPaid={false} />
                      <CertificateShareButton goalId={g.id} />
                    </div>
                  ))}
                </div>
              ) : null}
            </Section>
          ) : null}
        </>
      )}
    </div>
  );
}

function Section({
  title,
  children,
  collapsible,
  expanded,
  onToggle,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Eyebrow>{title}</Eyebrow>
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            data-testid="portfolio-completed-toggle"
            style={{
              background: "transparent",
              border: "1px solid var(--sa-hairline)",
              borderRadius: 3,
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
              color: "var(--sa-fg-primary)",
            }}
          >
            {expanded ? "Hide" : "Show"}
          </button>
        ) : null}
      </div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

interface PortfolioCardProps {
  goal: ReturnType<typeof useSikaGoals>["goals"][number];
  readOnly?: boolean;
  onPause?: () => void;
  onResume?: () => void;
  busy?: boolean;
}

function PortfolioCard({ goal, readOnly, onPause, onResume, busy }: PortfolioCardProps): JSX.Element {
  const target = Number(goal.target_amount ?? 0);
  const current = Number(goal.current_amount ?? 0);
  const pct = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
  const isCompleted = goal.status === "completed" || goal.status === "achieved";
  const isPaused = goal.status === "paused";

  return (
    <div
      data-testid={`portfolio-card-${goal.id}`}
      data-status={goal.status}
      style={{
        padding: 12,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
        background: "var(--sa-bg-base)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link
          to={`/sika?goal=${goal.id}`}
          style={{
            fontFamily: "var(--sa-font-display)",
            fontSize: 16,
            color: "var(--sa-fg-primary)",
            textDecoration: "none",
          }}
        >
          {goal.title}
        </Link>
        {isCompleted ? (
          <Badge variant="success">ARCHIVED</Badge>
        ) : isPaused ? (
          <Badge variant="info">PAUSED</Badge>
        ) : (
          <Badge variant="amber">ACTIVE</Badge>
        )}
      </div>

      <KV label="Progress" value={`${String(pct)}%`} first />
      <KV label="Target" value={`GH₵${target.toLocaleString()}`} />
      <KV label="Current" value={`GH₵${current.toLocaleString()}`} />
      {goal.timeline_weeks !== null && goal.timeline_weeks !== undefined ? (
        <KV label="Timeline" value={`${String(goal.timeline_weeks)} weeks`} />
      ) : null}
      {isCompleted && completedAt(goal) ? (
        <KV label="Completed" value={new Date(completedAt(goal)!).toLocaleDateString()} />
      ) : null}

      <div style={{ height: 4, background: "var(--sa-hairline)", borderRadius: 2 }}>
        <div
          style={{
            height: "100%",
            width: `${String(pct)}%`,
            background: isCompleted ? "var(--sa-accent-success, #6f6)" : "var(--sa-accent-saffron)",
            borderRadius: 2,
          }}
        />
      </div>

      {!readOnly && !isCompleted ? (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {isPaused ? (
            <button
              type="button"
              onClick={onResume}
              disabled={busy}
              data-testid={`portfolio-resume-${goal.id}`}
              style={cardBtnStyle}
            >
              {busy ? "Resuming…" : "Resume goal"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onPause}
              disabled={busy}
              data-testid={`portfolio-pause-${goal.id}`}
              style={cardBtnStyle}
            >
              {busy ? "Pausing…" : "Pause goal"}
            </button>
          )}
          <Link
            to={`/sika?goal=${goal.id}`}
            style={{ ...cardBtnStyle, textDecoration: "none", textAlign: "center", display: "inline-block" }}
          >
            Open →
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function completedAt(goal: object): string | null {
  // completed_at is added in SIKA-1 migration; typegen may lag.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (goal as any).completed_at ?? (goal as any).achieved_at ?? null;
  return typeof v === "string" ? v : null;
}

function KV({ label, value, first }: { label: string; value: string; first?: boolean }) {
  return (
    <HairlineRow first={first} padY={6}>
      <span style={{ flex: 1, fontSize: 12, color: "var(--sa-fg-secondary)" }}>{label}</span>
      <span style={{ fontFamily: "var(--sa-font-stat)", fontSize: 13 }}>{value}</span>
    </HairlineRow>
  );
}

const cardBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: "6px 10px",
  fontSize: 12,
  background: "transparent",
  border: "1px solid var(--sa-hairline)",
  borderRadius: 3,
  cursor: "pointer",
  color: "var(--sa-fg-primary)",
  fontFamily: "var(--sa-font-stat)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};
